/**
 * computeConfluence.ts -- MER Algo Footprints
 *
 * Phase 2 v1: pure function that scores deterministic directional
 * agreement across the existing engines.
 *
 * Inputs come from MarketContext (regime/gamma/smc/traps already
 * computed by buildSymbolContext) plus the live FlowSignal from the
 * institutional-flow sidecar. Output is a ConfluenceSignal that flows
 * to the AI thesis prompt.
 *
 * Design notes:
 *
 *   - PURE FUNCTION. No I/O, no side effects, no time-of-day adjustments.
 *
 *   - DOES NOT MODIFY INPUTS. Setup engine, liquidity engine, and the
 *     deterministic setup binding logic are untouched. This file only
 *     reads existing signals.
 *
 *   - DOES NOT CREATE NEW SETUPS. Confluence is a score over existing
 *     directional reads, not a setup generator.
 *
 *   - Weights are static per engine; the per-engine confidence scales
 *     the directional contribution within the weight budget. A regime
 *     that returns 80% confidence contributes more to the score than one
 *     at 40%, but neither can exceed the 25% weight ceiling.
 *
 *   - 'no_signal' and 'neutral' votes count as zero directional pressure
 *     but still occupy the engine's weight. The total weight in play
 *     remains 1.0 regardless of how many engines abstain -- the score
 *     just lands closer to zero.
 *
 *   - Confidence cap of 0.90 reflects that confluence is inferential
 *     across engines that can always disagree; we never claim certainty.
 */

import type { MarketContext } from '../../ai/context/contextBuilder';
import type { FlowSignal } from '../../institutionalFlow/schemas';
import type { RegimeResult, GammaResult, SMCResult, TrapSignal } from '../../../shared/types';
import {
  CONFLUENCE_CONFIDENCE_CAP,
  CONFLUENCE_WEIGHTS,
  type ConfluenceBias,
  type ConfluenceContribution,
  type ConfluenceEngineName,
  type ConfluenceSignal,
} from '../schemas';

// ── Per-engine bias derivation ──────────────────────────────────────────────
//
// Each function takes its engine's existing output shape and returns a
// (bias, confidence, reason) tuple. None of these functions mutate their
// input or call out of process.

interface Derived {
  bias:       ConfluenceBias;
  confidence: number;  // 0..1
  reason:     string;
}

/**
 * Regime engine returns a `dominant` regime + `posture` string + price
 * vs VWAP. The posture string is human-readable so we don't parse it;
 * we synthesize direction from (dominant, vwapDist).
 *
 * Mapping:
 *   - dominant 'noTrade'                  -> no_signal
 *   - dominant 'meanReversion'            -> direction OPPOSITE vwapDist sign
 *     (fade extremes: above VWAP -> sell, below VWAP -> buy)
 *   - dominant 'breakout' | 'trend'       -> direction MATCHING vwapDist sign
 *     (continuation: above VWAP -> bullish, below -> bearish)
 *
 * Engine self-confidence is regime.confidence (0..100), normalized to 0..1.
 */
function deriveRegime(r: RegimeResult | null): Derived {
  if (!r) return { bias: 'no_signal', confidence: 0, reason: 'No regime data' };

  if (r.dominant === 'noTrade') {
    return {
      bias: 'no_signal',
      confidence: r.confidence / 100,
      reason: `Regime '${r.label}' has no directional edge`,
    };
  }

  const fade = r.dominant === 'meanReversion';
  const above = r.vwapDist > 0;
  // meanReversion: fade VWAP extreme. breakout/trend: continue with VWAP.
  let bias: ConfluenceBias;
  if (fade) {
    bias = above ? 'bearish' : 'bullish';
  } else {
    bias = above ? 'bullish' : 'bearish';
  }
  return {
    bias,
    confidence: r.confidence / 100,
    reason: `Regime ${r.label} (${r.confidence}% conf), price ${above ? 'above' : 'below'} VWAP`,
  };
}

/**
 * SMC bias is already 'bullish' | 'bearish' | 'neutral'.
 * Confidence: major = 0.75, minor = 0.45, neutral = 0.
 */
function deriveSMC(s: SMCResult | null): Derived {
  if (!s) return { bias: 'no_signal', confidence: 0, reason: 'No SMC data' };
  if (s.bias === 'neutral') {
    return { bias: 'neutral', confidence: 0, reason: 'SMC bias neutral' };
  }
  const confidence = s.biasStrength === 'major' ? 0.75 : 0.45;
  return {
    bias: s.bias,
    confidence,
    reason: `SMC ${s.bias} ${s.biasStrength}; ${s.keySignal}`,
  };
}

/**
 * Gamma direction comes from regime + aboveFlip:
 *
 *   POSITIVE + aboveFlip   -> supportive bullish (dealers buy dips)
 *   POSITIVE + below flip  -> supportive bearish (mirrored, dealers sell rips)
 *   NEGATIVE + aboveFlip   -> amplifies upside (UPSIDE_EXPANSION territory) -> bullish
 *   NEGATIVE + below flip  -> amplifies downside -> bearish
 *   NEUTRAL                -> neutral
 *
 * Confidence comes from gamma.confidence (high/medium/low) mapped to
 * 0.75 / 0.50 / 0.30. When nearFlip is true we shave 0.10 because the
 * direction is fragile near the inflection point.
 */
function deriveGamma(g: GammaResult | null): Derived {
  if (!g) return { bias: 'no_signal', confidence: 0, reason: 'No gamma data' };
  // Legacy GammaRegime strings: 'Long Gamma / Pinning' | 'Short Gamma / Expansion' | 'Neutral / Transition'
  if (g.regime === 'Neutral / Transition') {
    return { bias: 'neutral', confidence: 0, reason: 'Gamma regime neutral / transition' };
  }
  const bias: ConfluenceBias = g.aboveFlip ? 'bullish' : 'bearish';
  const confMap: Record<string, number> = { High: 0.75, Medium: 0.50, Low: 0.30 };
  let confidence = confMap[g.confidence] ?? 0.40;
  if (g.nearFlip) confidence = Math.max(0, confidence - 0.10);
  const flipStr = g.gammaFlip != null ? g.gammaFlip.toFixed(2) : '?';
  return {
    bias,
    confidence,
    reason: `Gamma ${g.regime}, ${g.aboveFlip ? 'above' : 'below'} flip ${flipStr}${g.nearFlip ? ' (near flip)' : ''}`,
  };
}

/**
 * Institutional flow bias maps directly:
 *
 *   bullish_accumulation -> bullish
 *   bearish_distribution -> bearish
 *   neutral              -> neutral
 *   no_signal            -> no_signal
 *
 * Confidence is the engine's own (already 0..0.95).
 */
function deriveFlow(f: FlowSignal | null): Derived {
  if (!f) return { bias: 'no_signal', confidence: 0, reason: 'No flow data this cycle' };
  let bias: ConfluenceBias;
  switch (f.bias) {
    case 'bullish_accumulation': bias = 'bullish'; break;
    case 'bearish_distribution': bias = 'bearish'; break;
    case 'neutral':              bias = 'neutral'; break;
    default:                     bias = 'no_signal'; break;
  }
  const offPct = (f.metrics.offExchangePct * 100).toFixed(0);
  return {
    bias,
    confidence: f.confidence,
    reason: `Flow ${f.bias.replace(/_/g, ' ')} (${offPct}% off-exch, conf ${(f.confidence * 100).toFixed(0)}%)`,
  };
}

/**
 * Traps invert: a "bull trap" predicts bearish continuation; a "bear
 * trap" predicts bullish continuation. We pick the highest-severity
 * active trap as the engine's vote.
 *
 * Severity -> confidence:
 *   High   -> 0.70
 *   Medium -> 0.45
 *   Low    -> 0.20
 *
 * If multiple traps with conflicting sides exist, the highest severity
 * wins; ties resolve to 'neutral' because the directional signal is
 * confused.
 */
function deriveTraps(traps: TrapSignal[]): Derived {
  if (!traps.length) {
    return { bias: 'neutral', confidence: 0, reason: 'No active traps' };
  }
  const sevRank: Record<string, number> = { High: 3, Medium: 2, Low: 1, None: 0 };
  const sevConf: Record<string, number> = { High: 0.70, Medium: 0.45, Low: 0.20, None: 0 };

  let top: TrapSignal | null = null;
  let topRank = -1;
  let tiedSides = new Set<string>();
  for (const t of traps) {
    const rank = sevRank[t.severity] ?? 0;
    if (rank > topRank) {
      top = t;
      topRank = rank;
      tiedSides = new Set([t.side]);
    } else if (rank === topRank && top) {
      tiedSides.add(t.side);
    }
  }
  if (!top) return { bias: 'neutral', confidence: 0, reason: 'No active traps' };

  // Conflicting top-severity traps -> neutral
  if (tiedSides.size > 1) {
    return {
      bias: 'neutral',
      confidence: 0,
      reason: `${tiedSides.size} top-severity traps on conflicting sides`,
    };
  }

  // Bull trap predicts bearish; bear trap predicts bullish.
  let bias: ConfluenceBias;
  if (top.side === 'bull')      bias = 'bearish';
  else if (top.side === 'bear') bias = 'bullish';
  else                          bias = 'neutral';

  return {
    bias,
    confidence: sevConf[top.severity] ?? 0,
    reason: `${top.type}${top.level ? ` @ ${top.level.toFixed(2)}` : ''} (${top.severity})`,
  };
}

// ── Aggregator ──────────────────────────────────────────────────────────────

function biasSign(b: ConfluenceBias): -1 | 0 | 1 {
  if (b === 'bullish') return 1;
  if (b === 'bearish') return -1;
  return 0;
}

function biasFromScore(score: number): ConfluenceBias {
  // Threshold of 0.05 keeps tiny numerical drift from flipping to a
  // directional read. Anything inside [-0.05, +0.05] is neutral.
  if (score >  0.05) return 'bullish';
  if (score < -0.05) return 'bearish';
  return 'neutral';
}

/**
 * Compute the confluence signal for a symbol.
 *
 * @param symbol     Uppercase ticker
 * @param ctx        MarketContext snapshot (regime/gamma/smc/activeTraps)
 * @param flow       Current FlowSignal from the institutional-flow sidecar, or null
 */
export function computeConfluence(
  symbol: string,
  ctx:    MarketContext,
  flow:   FlowSignal | null,
): ConfluenceSignal {
  const sym = String(symbol || '').toUpperCase();

  // Derive each engine's directional read.
  const derived: Record<ConfluenceEngineName, Derived> = {
    regime:            deriveRegime(ctx.regime),
    smc:               deriveSMC(ctx.smc),
    gamma:             deriveGamma(ctx.gamma),
    institutionalFlow: deriveFlow(flow),
    traps:             deriveTraps(ctx.activeTraps),
  };

  // Build the contributions list in the engine order configured in
  // CONFLUENCE_WEIGHTS so the prompt + audit output stay stable.
  const contributions: ConfluenceContribution[] = (
    Object.keys(CONFLUENCE_WEIGHTS) as ConfluenceEngineName[]
  ).map((engine) => ({
    engine,
    bias:       derived[engine].bias,
    weight:     CONFLUENCE_WEIGHTS[engine],
    confidence: derived[engine].confidence,
    reason:     derived[engine].reason,
  }));

  // Weighted signed score: sum over engines of (sign * weight * confidence).
  // Range is mathematically [-1, +1] because weights sum to 1 and per-engine
  // confidence is bounded [0, 1].
  let score = 0;
  for (const c of contributions) {
    score += biasSign(c.bias) * c.weight * c.confidence;
  }
  // Clamp defensively against floating-point drift.
  score = Math.max(-1, Math.min(1, score));

  const bias = biasFromScore(score);

  // Confidence: scaled magnitude of the score, capped.
  // We use |score| (not raw weight sum) so that low-confidence agreement
  // produces low confluence confidence, while high-confidence agreement
  // approaches the cap.
  const confidence = Math.min(Math.abs(score), CONFLUENCE_CONFIDENCE_CAP);

  // Dissenters: engines whose bias contradicts the majority direction.
  // 'neutral' / 'no_signal' don't dissent; they abstain.
  const dissenting: ConfluenceEngineName[] = [];
  if (bias === 'bullish' || bias === 'bearish') {
    const oppositeBias: ConfluenceBias = bias === 'bullish' ? 'bearish' : 'bullish';
    for (const c of contributions) {
      if (c.bias === oppositeBias) dissenting.push(c.engine);
    }
  }

  // Human-readable evidence for the prompt.
  const evidence: string[] = [];
  const agreeing = contributions.filter(c => c.bias === bias && bias !== 'neutral');
  if (agreeing.length > 0) {
    evidence.push(`${agreeing.length} of 5 engines agree ${bias}: ${agreeing.map(a => a.engine).join(', ')}`);
  }
  if (dissenting.length > 0) {
    evidence.push(`Dissenting: ${dissenting.join(', ')}`);
  }
  const abstaining = contributions.filter(c => c.bias === 'neutral' || c.bias === 'no_signal');
  if (abstaining.length > 0) {
    evidence.push(`Abstaining: ${abstaining.map(a => `${a.engine} (${a.bias})`).join(', ')}`);
  }
  // Per-engine attribution
  for (const c of contributions) {
    evidence.push(`  ${c.engine} [${c.bias}, w=${(c.weight * 100).toFixed(0)}%, conf=${(c.confidence * 100).toFixed(0)}%]: ${c.reason}`);
  }

  return {
    symbol:        sym,
    bias,
    score,
    confidence,
    contributions,
    dissenting,
    evidence,
    timestamp:     Date.now(),
  };
}
